import is from '@sindresorhus/is';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useDebounceValue } from 'usehooks-ts';

import { ApplicationContext } from '../context/context';
import {
    profileVisibilityUserSearchResponseSchema,
    userProfileVisibilityResponseSchema,
    userProfileVisibilityTemplateResponseSchema,
} from '../data-contract/profile-visibility';

import type {
    ProfileVisibilityUserSearchResponse,
    UserProfileVisibilityRequest,
    UserProfileVisibilityResponse,
    UserProfileVisibilityTemplateResponse,
} from '../data-contract/profile-visibility';
import type { Pathname } from '../http-fetcher/http-fetcher';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

const createPathname = (ownerId: string): Pathname => `/users/${ownerId}/visibility`;

const createUserSearchPathname = (ownerId: string): Pathname => `/users/${ownerId}/visibility/search`;

const createTemplatePathname = (ownerId: string): Pathname => `/users/${ownerId}/visibility/template`;

export const useUserProfileVisibilityQuery = (ownerId: string): UseQueryResult<UserProfileVisibilityResponse> => {
    const profileVisibilityUrl = createPathname(ownerId);
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userProfileVisibilityResponseSchema, {
                pathname: profileVisibilityUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [profileVisibilityUrl, 'GET'],
    });
};

const debounceSearchQueryMs = 100;

export const useProfileVisibilityUserSearchQuery = (
    ownerId: string,
    searchTerm: string,
): UseQueryResult<ProfileVisibilityUserSearchResponse, Error> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const pathname = createUserSearchPathname(ownerId);
    const [searchTermDebounced] = useDebounceValue(searchTerm, debounceSearchQueryMs);

    const enabled =
        is.nonEmptyStringAndNotWhitespace(ownerId) && is.nonEmptyStringAndNotWhitespace(searchTermDebounced);

    const requestOptions = {
        searchParams: { searchTerm: searchTermDebounced },
    };

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(profileVisibilityUserSearchResponseSchema, {
                pathname,
                requestOptions,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [pathname, 'get', profileVisibilityUserSearchResponseSchema, requestOptions],
    });
};

export const useUserProfileVisibilityTemplateQuery = (
    ownerId: string,
): UseQueryResult<UserProfileVisibilityTemplateResponse> => {
    const profileVisibilityTemplateUrl = createTemplatePathname(ownerId);
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(userProfileVisibilityTemplateResponseSchema, {
                pathname: profileVisibilityTemplateUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [profileVisibilityTemplateUrl, 'GET'],
    });
};

export type UserProfileVisibilityMutation = UseMutationResult<
    UserProfileVisibilityResponse,
    Error,
    UserProfileVisibilityRequest
>;

export const useUserProfileVisibilityMutation = (id: string): UserProfileVisibilityMutation => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const queryClient = useQueryClient();

    const pathname: Pathname = createPathname(id);

    return useMutation({
        mutationFn: async (
            profileVisibilityData: UserProfileVisibilityRequest,
        ): Promise<UserProfileVisibilityResponse> => {
            const result = await validatingCobrainerApiFetcher.postJson(userProfileVisibilityResponseSchema, {
                pathname,
                payload: profileVisibilityData,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSettled: () => {
            queryClient.invalidateQueries({ queryKey: [pathname] });
        },
    });
};
